import { PageTitle } from "@/components/custom/PageTitle"
import { useForm } from "react-hook-form"
import { z } from "zod"
import { zodResolver } from "@hookform/resolvers/zod"
import { Form } from "@/components/ui/form"
import { Button } from "@/components/ui/button"
import { useSelector, useDispatch } from "react-redux"
import { AppDispatch, RootState } from "@/redux"
import { useNavigate } from "react-router-dom"
import { createOrganization } from "@/services/organizationService"
import { TextInputField } from "@/components/custom/InputField/TextInputField"
import { useEffect } from "react";
import { getAllInvitedUsersInOrganization, getAllUsersInOrganization } from "@/services/userService"
import { getAllApplications } from "@/services/applicationsService"
import { getAllWorkflows } from "@/services/workflowService"
import { getInvoices, getOneTimeFees, getSubscription } from "@/services/billingService"
import { getCredentials } from "@/services/credentialsService"
import { getAllTransformationTemplates } from "@/services/transformationService"

const formSchema = z.object({
  name: z.string().min(3, {
    message: 'Organization name must be at least 3 characters.'
  })
})

export const NoOrganization = () => {
  const navigate = useNavigate()
  const dispatch: AppDispatch = useDispatch()
  const { token, userData } = useSelector((state: RootState) => state.user)
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
    },
  })

  useEffect(() => {
    if (userData?.currentOrganization) {
      navigate('/router')
    }
  }, [userData, navigate])

  const onFormSubmit = async (
    values: z.infer<typeof formSchema>
  ) => {
    if (!token) {
      return
    }
    const response = await dispatch(createOrganization(token, values))
    if (response.data) {
      form.reset({ name: "" })
      // Fetch everything
      await dispatch(getAllUsersInOrganization(token))
      await dispatch(getAllInvitedUsersInOrganization(token))
      await dispatch(getAllApplications(token))
      await dispatch(getAllWorkflows(token))
      await dispatch(getCredentials(token))
      await dispatch(getAllTransformationTemplates(token))
      // not for 'user' role
      if (userData?.role !== 'user') {
        const orgId = userData?.currentOrganization?.id as string
        dispatch(getSubscription(token, orgId))
        dispatch(getInvoices(token, orgId))
        dispatch(getOneTimeFees(token, orgId))
      }
      navigate('/router')
    }
  }

  return (
    <div className="protectedPageLayout">
      <PageTitle title="You're currently not part of any Organization" />
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onFormSubmit)}
          className="space-y-8 flex flex-col items-start"
        >
          <TextInputField
            form={form}
            schema={formSchema}
            name="name"
            label="Organization Name"
            error={!!form.formState.errors.name}
            placeholder="XYZ"
            required={true}
          />
          <Button type="submit" className="mb-2 font-semibold">
            Create Organization
          </Button>
        </form>
      </Form>
    </div>
  )
}
